/**
 * Iter 70 / RBAC Rebuild Phase 3 — Roles & Access Control admin UI.
 *
 * Tabs:
 *   1. Legacy Role Matrix — existing per-role module×action grid (unchanged)
 *   2. Department Profiles — V2 two-axis permission profiles per department
 *   3. Senior Profiles    — named above-department profiles assignable to individuals
 *   4. Effective Access   — "view as" any employee: show their fully-resolved V2 permissions
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, Plus, Trash2, Save, Loader2, X, Check, Copy, Users as UsersIcon, ChevronRight, Eye, Building2, Star, AlertTriangle, Briefcase, History } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { formatDate as _fmtD, formatDateTime as _fmtDT } from '../../lib/format';
import { useAuth } from '../../contexts/AuthContext';
import { DepartmentReconciliationPanel } from './DepartmentReconciliationPanel';
import { useUrlTab } from '../../lib/hooks/useUrlState';

export default function RolesAccessControl() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const canEdit = (() => {
    const role = (user?.role || '').toLowerCase();
    const dept = (user?.department || '').toUpperCase();
    return role === 'super_admin' || dept === 'IT';
  })();
  const [mainTab, setMainTab] = useUrlTab('tab', 'dept'); // 'dept' | 'senior' | 'consultant' | 'reconcile' | 'effective' | 'exceptions'
  const [orphanCount, setOrphanCount] = useState(0);
  const [rbacMode, setRbacMode] = useState(null);

  useEffect(() => {
    if ((user?.role || '').toLowerCase() !== 'super_admin') return;
    apiGet('/rbac/v2/flag').then(r => setRbacMode(r?.rbac_mode || null)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if ((user?.role || '').toLowerCase() !== 'super_admin') return;
    apiGet('/rbac/v2/diagnostics/orphan-departments')
      .then(r => {
        const criticals = (r?.empty_profiles || []).filter(p => p.severity === 'critical').length;
        setOrphanCount((r?.orphan_user_count || 0) + criticals);
      })
      .catch(() => {});
  }, [user]);

  const [catalog, setCatalog] = useState({ modules: [], actions: [] });
  const [roles, setRoles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [departments, setDepartments] = useState([]);
  const [grades, setGrades] = useState([]);
  // Users assigned to the currently selected role + reassignment UI
  const [roleUsers, setRoleUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [reassignFor, setReassignFor] = useState(null); // user_id being reassigned
  const [reassignTarget, setReassignTarget] = useState('');
  // Per-user permission override editor (modal)
  const [permEditFor, setPermEditFor] = useState(null); // { user_id, name, email, role_name }
  const [permEditMatrix, setPermEditMatrix] = useState({}); // { module_key: { action: bool } }
  const [permEditRoleBase, setPermEditRoleBase] = useState({}); // for diff display
  const [permEditOverrides, setPermEditOverrides] = useState({}); // existing overrides
  const [permEditLoading, setPermEditLoading] = useState(false);
  const [permEditSaving, setPermEditSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cat, rs, masters] = await Promise.all([
        apiGet('/rbac/catalog'),
        apiGet('/rbac/roles'),
        apiGet('/config-masters').catch(() => ({ masters: {} })),
      ]);
      setCatalog(cat || { modules: [], actions: [] });
      setRoles(rs?.rows || []);
      // Iter 293: fetch departments from /rbac/departments (always includes all depts
      // incl. Project Management) instead of config-masters which was incomplete.
      apiGet('/rbac/departments')
        .then(d => setDepartments(d?.departments || []))
        .catch(() => setDepartments(masters?.masters?.departments || []));
      setGrades(masters?.masters?.employee_grades || []);
      if (!selectedId && rs?.rows?.[0]) setSelectedId(rs.rows[0].id);
    } catch (e) {
      toast.error('Failed to load roles');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Whenever the selected role changes, load its assigned users.
  useEffect(() => {
    if (!selectedId) { setRoleUsers([]); return; }
    setLoadingUsers(true);
    apiGet(`/rbac/roles/${selectedId}/users`)
      .then(r => setRoleUsers(r?.users || []))
      .catch(() => setRoleUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [selectedId]);

  const reassignUserRole = async (userId, targetRoleId) => {
    if (!targetRoleId) { toast.error('Pick a target role'); return; }
    try {
      if (targetRoleId === '_unassigned') {
        await apiDel(`/rbac/users/${userId}/role`);
      } else {
        await apiPut(`/rbac/users/${userId}/role`, { role_id: targetRoleId });
      }
      toast.success('Role reassigned');
      setReassignFor(null);
      setReassignTarget('');
      // Refresh this role's users list
      const r = await apiGet(`/rbac/roles/${selectedId}/users`);
      setRoleUsers(r?.users || []);
      // ALSO reload full roles list to clear stale local permission edits
      const rolesRes = await apiGet('/rbac/roles');
      setRoles(rolesRes?.rows || rolesRes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reassignment failed');
    }
  };

  // ── Iter 105.2 — Per-user permission editor ────────────────────────────
  const openPermEditor = async (u) => {
    setPermEditFor({ user_id: u.user_id, name: u.name || u.email, email: u.email, role_name: u.role_name || '—' });
    setPermEditLoading(true);
    setPermEditMatrix({});
    setPermEditRoleBase({});
    setPermEditOverrides({});
    try {
      const res = await apiGet(`/rbac/users/${u.user_id}/permissions`);
      setPermEditMatrix(res?.effective || {});
      setPermEditRoleBase(res?.from_role || {});
      setPermEditOverrides(res?.overrides || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load user permissions');
      setPermEditFor(null);
    } finally {
      setPermEditLoading(false);
    }
  };

  const togglePermEditCell = (modKey, action) => {
    setPermEditMatrix((m) => ({
      ...m,
      [modKey]: { ...(m[modKey] || {}), [action]: !m[modKey]?.[action] },
    }));
  };

  const savePermEdit = async () => {
    if (!permEditFor) return;
    setPermEditSaving(true);
    try {
      const res = await apiPut(`/rbac/users/${permEditFor.user_id}/permissions`, { permissions: permEditMatrix });
      const cnt = res?.override_count || 0;
      toast.success(cnt > 0 ? `Saved ${cnt} custom override${cnt === 1 ? '' : 's'}` : 'Cleared all overrides — user follows the role');
      setPermEditFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setPermEditSaving(false);
    }
  };

  const resetPermEdit = async () => {
    if (!permEditFor) return;
    if (!window.confirm(`Reset ${permEditFor.name}'s permissions to follow the role exactly?\n\nAll custom cell-level overrides will be removed.`)) return;
    setPermEditSaving(true);
    try {
      await apiDel(`/rbac/users/${permEditFor.user_id}/permissions`);
      toast.success('Reset to role defaults');
      setPermEditFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setPermEditSaving(false);
    }
  };

  const selected = roles.find((r) => r.id === selectedId);
  const isSuperAdminSelected = selectedId === 'role_super_admin';

  // Categories for the matrix (preserves order from catalog)
  const grouped = useMemo(() => {
    const map = {};
    for (const m of catalog.modules || []) {
      if (!map[m.category]) map[m.category] = [];
      map[m.category].push(m);
    }
    return map;
  }, [catalog.modules]);

  const filteredRoles = roles.filter((r) =>
    !search || r.name.toLowerCase().includes(search.toLowerCase())
    || (r.department || '').toLowerCase().includes(search.toLowerCase()));

  // === Mutations ===
  const togglePerm = (modKey, action) => {
    if (!canEdit || isSuperAdminSelected) return;
    setRoles((rs) => rs.map((r) => {
      if (r.id !== selectedId) return r;
      const next = { ...(r.permissions || {}) };
      next[modKey] = { ...(next[modKey] || {}) };
      next[modKey][action] = !next[modKey][action];
      return { ...r, permissions: next, _dirty: true };
    }));
  };

  const setAllForRow = (modKey, value) => {
    if (!canEdit || isSuperAdminSelected) return;
    setRoles((rs) => rs.map((r) => {
      if (r.id !== selectedId) return r;
      const next = { ...(r.permissions || {}) };
      next[modKey] = Object.fromEntries(catalog.actions.map((a) => [a, value]));
      return { ...r, permissions: next, _dirty: true };
    }));
  };

  // Approval limit helper
  const setApproveLimit = (modKey, value) =>
    setRoles((rs) => rs.map((r) => r.id !== selectedId ? r : {
      ...r,
      permissions: {
        ...r.permissions,
        [modKey]: { ...(r.permissions?.[modKey] || {}), approve_limit: value === '' ? null : parseFloat(value) || null }
      },
      _dirty: true,
    }));

  const setAllForCol = (action, value) => {
    if (!canEdit || isSuperAdminSelected) return;
    setRoles((rs) => rs.map((r) => {
      if (r.id !== selectedId) return r;
      const next = { ...(r.permissions || {}) };
      for (const m of catalog.modules) {
        next[m.key] = { ...(next[m.key] || {}), [action]: value };
      }
      return { ...r, permissions: next, _dirty: true };
    }));
  };

  const setAllForCategory = (cat, value) => {
    if (!canEdit || isSuperAdminSelected) return;
    const modsInCat = (catalog.modules || []).filter((m) => m.category === cat);
    setRoles((rs) => rs.map((r) => {
      if (r.id !== selectedId) return r;
      const next = { ...(r.permissions || {}) };
      for (const m of modsInCat) {
        next[m.key] = Object.fromEntries(catalog.actions.map((a) => [a, value]));
      }
      return { ...r, permissions: next, _dirty: true };
    }));
  };

  // true when EVERY action × EVERY module in a category is granted
  const isCategoryFullyChecked = (cat) => {
    if (isSuperAdminSelected) return true;
    if (!selected) return false;
    const modsInCat = (catalog.modules || []).filter((m) => m.category === cat);
    if (modsInCat.length === 0) return false;
    return modsInCat.every((m) =>
      catalog.actions.every((a) => !!selected.permissions?.[m.key]?.[a])
    );
  };

  const updateMeta = (field, value) => {
    setRoles((rs) => rs.map((r) => (r.id === selectedId ? { ...r, [field]: value, _dirty: true } : r)));
  };

  const [sodViolations, setSodViolations] = useState([]);
  const [sodAcknowledged, setSodAcknowledged] = useState(false);

  const saveRole = async (acknowledgeOverride = false) => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await apiPut(`/rbac/roles/${selected.id}`, {
        name: selected.name,
        grade: selected.grade,
        department: selected.department,
        description: selected.description,
        permissions: selected.permissions,
        acknowledge_sod: acknowledgeOverride || sodAcknowledged,
      });
      // BUILD 2: SoD warning — backend returns sod_warning flag
      if (res?.sod_warning) {
        setSodViolations(res.violations || []);
        setSaving(false);
        return;
      }
      setSodViolations([]);
      setSodAcknowledged(false);
      toast.success(`${selected.name} saved`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const createCustomRole = async () => {
    const name = window.prompt('Name for the new custom role?');
    if (!name) return;
    try {
      const r = await apiPost('/rbac/roles', {
        name, grade: null, department: null, description: 'Custom role',
        permissions: {},
      });
      toast.success(`Created '${name}'`);
      setSelectedId(r.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create role');
    }
  };

  const cloneRole = async () => {
    if (!selected) return;
    const name = window.prompt(`Clone "${selected.name}" as:`, `${selected.name} (Copy)`);
    if (!name) return;
    try {
      const r = await apiPost('/rbac/roles', {
        name,
        grade: selected.grade,
        department: selected.department,
        description: `Cloned from ${selected.name}`,
        permissions: selected.permissions,
      });
      toast.success(`Cloned as '${name}'`);
      setSelectedId(r.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not clone');
    }
  };

  const deleteRole = async () => {
    if (!selected || selected.is_system) return;
    if (!window.confirm(`Delete role "${selected.name}"?\n\nUsers currently on this role will be detached and need a new role assigned.`)) return;
    try {
      const r = await apiDel(`/rbac/roles/${selected.id}`);
      toast.success(`Deleted (${r.detached_users || 0} user${r.detached_users === 1 ? '' : 's'} detached)`);
      setSelectedId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) {
    return <div className="p-10 text-center"><Loader2 size={20} className="animate-spin inline mr-2" />Loading roles…</div>;
  }

  return (
    <div className="p-6 md:p-8" data-testid="rbac-page">
      <button onClick={() => navigate('/app/settings')} className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#3b4872] flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Back to Settings</button>
      <PageHeader
        overline="Settings · Security"
        title="Roles & Access Control"
        subtitle="V2 department, senior & consultant profiles · per-user exceptions · effective-access preview."
        icon={ShieldAlert}
      />

      {rbacMode === 'shadow' && (
        <div className="mt-4 rounded-sm border border-rose-300 bg-rose-50 px-4 py-3 flex items-start gap-3" data-testid="rbac-mode-shadow-banner" role="alert">
          <AlertTriangle size={18} className="text-rose-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-sm font-display font-bold text-rose-800">Permissions are NOT being enforced (Shadow mode)</div>
            <div className="text-xs text-rose-700 mt-0.5">
              Every permission check currently allows access — the API serves any request regardless of a user's role or department.
              Denials are only logged (for profile building). Switch to <strong>Enforce</strong> once department profiles are complete to close this exposure.
            </div>
          </div>
        </div>
      )}
      {rbacMode === 'enforce' && (
        <div className="mt-4 rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 flex items-center gap-2" data-testid="rbac-mode-enforce-banner">
          <Check size={15} className="text-emerald-600 shrink-0" />
          <div className="text-xs text-emerald-800">
            <strong>Enforce mode active</strong> — permission checks are being enforced.
          </div>
        </div>
      )}

      {!canEdit && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 text-xs text-amber-900">
          <strong>Read-only view.</strong> Only Super Admin and IT department can edit roles.
        </div>
      )}

      {orphanCount > 0 && (
        <button onClick={() => setMainTab('reconcile')} data-testid="rbac-orphan-banner"
          className="mt-4 w-full text-left bg-rose-50 border border-rose-300 rounded-sm px-4 py-3 text-xs text-rose-900 hover:bg-rose-100 transition-colors">
          <strong>{orphanCount} RBAC data problem(s) detected</strong> — users in departments with no permission
          profile, or profiles that grant nothing. Those users see no modules and every API returns 403. Open
          Department Reconciliation to fix →
        </button>
      )}

      {/* ── Main tab bar — V2 only ── */}
      <div className="flex items-center gap-1 border-b border-slate-200 mt-4 mb-4">
        {[
          { key: 'dept',        label: 'Department Profiles', icon: Building2 },
          { key: 'senior',      label: 'Senior Profiles', icon: Star },
          { key: 'consultant',  label: 'Consultant Profiles', icon: Briefcase },
          { key: 'reconcile',   label: 'Reconciliation', icon: AlertTriangle },
          { key: 'effective',   label: 'View As', icon: Eye },
          { key: 'impact',      label: 'Enforce Impact', icon: ShieldAlert },
          { key: 'exceptions',  label: 'Exceptions', icon: AlertTriangle },
          { key: 'audit',       label: 'Audit Trail', icon: History },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setMainTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-display font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${mainTab === key ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`rbac-main-tab-${key}`}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {/* ── Department Profiles (V2) ── */}
      {mainTab === 'dept' && <DepartmentProfilesPanel canEdit={canEdit} />}

      {/* ── Senior Profiles (V2) ── */}
      {mainTab === 'senior' && <SeniorProfilesPanel canEdit={canEdit} />}

      {/* ── Effective Access "View As" ── */}
      {mainTab === 'effective' && <EffectiveAccessPanel />}

      {/* ── Enforce Impact Preview (§3) ── */}
      {mainTab === 'impact' && (
        <ImpactPreviewPanel
          isSuperAdmin={(user?.role || '').toLowerCase() === 'super_admin'}
          onModeChanged={(m) => setRbacMode(m)}
        />
      )}

      {/* ── Consultant Profiles (V2) ── */}
      {mainTab === 'consultant' && <ConsultantProfilesPanel canEdit={canEdit} />}

      {/* ── Department Reconciliation (orphan departments) ── */}
      {mainTab === 'reconcile' && <DepartmentReconciliationPanel canEdit={canEdit} />}

      {/* ── Exceptions (Per-user overrides audit) ── */}
      {mainTab === 'exceptions' && <ExceptionsPanel canEdit={canEdit} />}

      {/* ── Audit Trail (Department profile change history, all departments) ── */}
      {mainTab === 'audit' && <AuditTrailPanel />}
    </div>
  );
}


// ─── V2 Panel: Department Profiles ───────────────────────────────────────────
const LEVEL_OPTIONS = ['none', 'view', 'edit'];

function DepartmentProfilesPanel({ canEdit }) {
  const { checkAuth } = useAuth() || {};
  const [profiles, setProfiles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [allDepts, setAllDepts] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [editing, setEditing] = useState({});   // node_key → {level, export, delete, approve_limit}
  const [saving, setSaving] = useState(false);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [showNewDeptSelect, setShowNewDeptSelect] = useState(false);

  useEffect(() => {
    apiGet('/rbac/v2/department-profiles').then(r => setProfiles(r.profiles || [])).catch(() => {});
    apiGet('/rbac/v2/catalog').then(r => setCatalog(r.nodes?.filter(n => !n.is_open) || [])).catch(() => {});
    // Load all departments from master so we show all depts (configured + not)
    apiGet('/rbac/departments').then(r => setAllDepts(r?.departments || [])).catch(() => {});
  }, []);

  const configuredDepts = new Set(profiles.map(p => p.department));

  const openProfile = (deptName) => {
    if (!catalog.length) { toast.error('Permission catalog still loading — try again in a moment'); return; }
    setSelectedDept(deptName);
    const existing = profiles.find(p => p.department === deptName);
    const perms = existing?.permissions || {};
    const init = {};
    catalog.forEach(n => { init[n.key] = perms[n.key] || { level: 'none', export: false, delete: false, approve_limit: null }; });
    setEditing(init);
    setShowNewDeptSelect(false);
  };

  const grantedNow = Object.values(editing).filter(v => v?.level === 'view' || v?.level === 'edit').length;
  const storedGranted = Object.values(profiles.find(p => p.department === selectedDept)?.permissions || {})
    .filter(v => v?.level === 'view' || v?.level === 'edit').length;

  const doSave = async (confirmClear = false) => {
    setSaving(true);
    try {
      const encoded = encodeURIComponent(selectedDept);
      const qs = confirmClear ? '?confirm_clear=true' : '';
      await apiPut(`/rbac/v2/department-profiles/${encoded}${qs}`, { permissions: editing });
      toast.success(`${selectedDept} permissions saved — all users in this department will see updated modules within 60 seconds`);
      const r = await apiGet('/rbac/v2/department-profiles');
      setProfiles(r.profiles || []);
      // Immediately refresh the saving admin's own session
      try { await checkAuth?.(); } catch (_) {}
      setClearConfirm(false);
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || e?.message || 'Save failed'); }
    setSaving(false);
  };

  const save = async () => {
    if (!catalog.length || !Object.keys(editing).length) {
      toast.error('Nothing loaded to save — re-open the department profile');
      return;
    }
    if (grantedNow === 0 && storedGranted > 0) { setClearConfirm(true); return; }
    await doSave(false);
  };

  const byModule = catalog.reduce((acc, n) => {
    (acc[n.parent_module] = acc[n.parent_module] || []).push(n);
    return acc;
  }, {});

  // Merge: all depts from master + any profile depts not in master
  const allDeptNames = Array.from(new Set([
    ...allDepts,
    ...profiles.map(p => p.department),
  ])).sort();

  return (
    <div className="space-y-4" data-testid="dept-profiles-panel">
      <div className="flex items-start gap-4">
        <div className="w-56 shrink-0 bg-white border border-slate-200 rounded-sm p-3">
          <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Departments</div>
          <p className="text-[9px] text-slate-400 mb-2 leading-snug px-0.5">
            Add, rename, or delete in{' '}
            <a href="/app/settings/master-lists" className="text-[#3b4872] font-bold underline" data-testid="dept-master-link">
              Master Lists
            </a>
            . This page manages permissions only.
          </p>
          {allDeptNames.map(deptName => {
            const isConfigured = configuredDepts.has(deptName);
            return (
              <button key={deptName} onClick={() => openProfile(deptName)}
                className={`w-full text-left px-2 py-1.5 rounded-sm text-xs font-display font-semibold mb-0.5 flex items-center justify-between ${selectedDept === deptName ? 'bg-[#3b4872] text-white' : 'hover:bg-slate-50 text-slate-700'}`}
                data-testid={`dept-profile-${deptName}`}>
                <span>{deptName}</span>
                {isConfigured
                  ? <span className={`text-[9px] px-1 py-0.5 rounded ${selectedDept === deptName ? 'bg-white/20' : 'bg-emerald-100 text-emerald-700'}`}>✓</span>
                  : <span className={`text-[9px] px-1 py-0.5 rounded ${selectedDept === deptName ? 'bg-white/20' : 'bg-slate-100 text-slate-400'}`}>–</span>
                }
              </button>
            );
          })}
          {canEdit && !showNewDeptSelect && (
            <button onClick={() => setShowNewDeptSelect(true)}
              className="w-full mt-2 text-[10px] text-[#3b4872] font-display font-bold uppercase tracking-wider flex items-center gap-1 hover:underline"
              data-testid="dept-profile-new">
              <Plus size={11} /> Set permissions for a department →
            </button>
          )}
          {showNewDeptSelect && (
            <div className="mt-2">
              <select
                className="w-full text-xs border border-slate-300 rounded-sm px-2 py-1 focus:outline-none focus:border-[#3b4872] bg-white"
                defaultValue=""
                onChange={e => { if (e.target.value) openProfile(e.target.value); }}
                data-testid="dept-profile-new-select"
                autoFocus
              >
                <option value="">— pick department —</option>
                {allDeptNames.filter(d => !configuredDepts.has(d)).map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <button onClick={() => setShowNewDeptSelect(false)} className="mt-1 text-[10px] text-slate-400 hover:text-slate-600">cancel</button>
            </div>
          )}
        </div>
        {selectedDept ? (
          <div className="flex-1 bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="font-display font-bold text-sm flex items-center gap-2">
                {selectedDept}
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${grantedNow === 0 ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}
                      data-testid="dp-granted-count">
                  {grantedNow} granted
                </span>
              </div>
              {canEdit && (
                <button onClick={save} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#3b4872] text-white text-xs font-display font-bold rounded-sm disabled:opacity-50" data-testid="dept-profile-save">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                </button>
              )}
            </div>
            {grantedNow === 0 && (
              <div className="px-4 py-2 bg-rose-50 border-b border-rose-200 text-[11px] text-rose-800" data-testid="dp-empty-warning">
                This profile grants nothing — every user in <strong>{selectedDept}</strong> would see no modules and get 403 on every API.
              </div>
            )}
            {clearConfirm && (
              <div className="px-4 py-3 bg-rose-50 border-b border-rose-300 text-xs text-rose-900 flex items-center gap-3" data-testid="dp-clear-confirm">
                <span className="flex-1">
                  You are about to remove <strong>all {storedGranted}</strong> permission(s) from {selectedDept}. Every user in
                  that department loses every module. Continue?
                </span>
                <button onClick={() => doSave(true)} disabled={saving}
                  className="px-3 py-1.5 bg-rose-600 text-white text-[11px] font-display font-bold uppercase tracking-wider rounded-sm disabled:opacity-50"
                  data-testid="dp-clear-confirm-yes">Revoke everything</button>
                <button onClick={() => setClearConfirm(false)}
                  className="px-3 py-1.5 border border-slate-300 text-[11px] font-display font-bold uppercase tracking-wider rounded-sm"
                  data-testid="dp-clear-confirm-no">Cancel</button>
              </div>
            )}
            <div className="overflow-y-auto max-h-[60vh]">
              {Object.entries(byModule).map(([mod, nodes]) => (
                <div key={mod} className="border-b border-slate-100">
                  <div className="px-4 py-1.5 bg-slate-50 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">{mod}</div>
                  {nodes.map(n => {
                    const e = editing[n.key] || { level: 'none', export: false, delete: false };
                    return (
                      <div key={n.key} className="flex items-center gap-3 px-4 py-2 border-b border-slate-50 hover:bg-slate-50/50">
                        <div className="flex-1 text-xs font-display font-semibold text-slate-700">{n.label} <span className="text-[10px] font-mono text-slate-400 ml-1">{n.key}</span></div>
                        <select value={e.level} onChange={ev => setEditing(prev => ({ ...prev, [n.key]: { ...e, level: ev.target.value } }))}
                          disabled={!canEdit}
                          className="text-xs border border-slate-200 rounded-sm px-2 py-0.5 bg-white focus:outline-none focus:border-[#3b4872]"
                          data-testid={`dp-level-${n.key}`}>
                          {LEVEL_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                        {n.supports_export && (
                          <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
                            <input type="checkbox" checked={e.export} onChange={ev => setEditing(prev => ({ ...prev, [n.key]: { ...e, export: ev.target.checked } }))} disabled={!canEdit} className="accent-[#3b4872]" data-testid={`dp-export-${n.key}`} /> Export
                          </label>
                        )}
                        {n.supports_delete && (
                          <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
                            <input type="checkbox" checked={e.delete} onChange={ev => setEditing(prev => ({ ...prev, [n.key]: { ...e, delete: ev.target.checked } }))} disabled={!canEdit} className="accent-rose-600" data-testid={`dp-delete-${n.key}`} /> Delete
                          </label>
                        )}
                        {e.level === 'edit' && canEdit && (
                          <div className="flex items-center gap-1 text-[10px] text-slate-500">
                            <span className="font-display font-semibold text-slate-400 whitespace-nowrap">Approve Limit (₹)</span>
                            <input
                              type="number" min="0" step="10000"
                              value={e.approve_limit ?? ''}
                              onChange={ev => setEditing(prev => ({ ...prev, [n.key]: { ...e, approve_limit: ev.target.value === '' ? null : parseFloat(ev.target.value) || null } }))}
                              placeholder="Unlimited"
                              className="w-24 text-xs border border-slate-200 rounded-sm px-2 py-0.5 focus:outline-none focus:border-[#3b4872]"
                              data-testid={`dp-approve-limit-${n.key}`}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center py-16 text-slate-400 text-sm">Select a department to view / edit its V2 permission profile.</div>
        )}
      </div>
    </div>
  );
}

// ─── V2 Panel: Senior Profiles ────────────────────────────────────────────────
function SeniorProfilesPanel({ canEdit }) {
  const [profiles, setProfiles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [selectedName, setSelectedName] = useState(null);
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet('/rbac/v2/senior-profiles').then(r => setProfiles(r.profiles || [])).catch(() => {});
    apiGet('/rbac/v2/catalog').then(r => setCatalog(r.nodes?.filter(n => !n.is_open) || [])).catch(() => {});
  }, []);

  const openProfile = (p) => {
    setSelectedName(p.name);
    const perms = p.permissions || {};
    const init = {};
    catalog.forEach(n => { init[n.key] = perms[n.key] || { level: 'none', export: false, delete: false }; });
    setEditing(init);
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiPut(`/rbac/v2/senior-profiles/${encodeURIComponent(selectedName)}`, { permissions: editing });
      toast.success(`Saved senior profile: ${selectedName}`);
      const r = await apiGet('/rbac/v2/senior-profiles');
      setProfiles(r.profiles || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const byModule = catalog.reduce((acc, n) => {
    (acc[n.parent_module] = acc[n.parent_module] || []).push(n);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="senior-profiles-panel">
      <p className="text-xs text-slate-500">Senior-management profiles grant cross-department access above the department baseline. Assign to individuals via Employee Master.</p>
      <div className="flex items-start gap-4">
        <div className="w-56 shrink-0 bg-white border border-slate-200 rounded-sm p-3">
          <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-2">Profiles</div>
          {profiles.map(p => (
            <button key={p.name} onClick={() => openProfile(p)}
              className={`w-full text-left px-2 py-1.5 rounded-sm text-xs font-display font-semibold mb-0.5 ${selectedName === p.name ? 'bg-[#3b4872] text-white' : 'hover:bg-slate-50 text-slate-700'}`}
              data-testid={`senior-profile-${p.name}`}>
              {p.name}
            </button>
          ))}
          {canEdit && (
            <button onClick={() => {
              const n = window.prompt('Profile name (e.g. Executive, Finance Head):');
              if (!n) return;
              const init = {};
              catalog.forEach(nd => { init[nd.key] = { level: 'none', export: false, delete: false }; });
              setSelectedName(n); setEditing(init);
            }}
              className="w-full mt-2 text-[10px] text-[#3b4872] font-display font-bold uppercase tracking-wider flex items-center gap-1 hover:underline"
              data-testid="senior-profile-new">
              <Plus size={11} /> New
            </button>
          )}
        </div>
        {selectedName ? (
          <div className="flex-1 bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="font-display font-bold text-sm">{selectedName}</div>
              {canEdit && (
                <button onClick={save} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#3b4872] text-white text-xs font-display font-bold rounded-sm disabled:opacity-50" data-testid="senior-profile-save">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                </button>
              )}
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              {Object.entries(byModule).map(([mod, nodes]) => (
                <div key={mod} className="border-b border-slate-100">
                  <div className="px-4 py-1.5 bg-slate-50 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">{mod}</div>
                  {nodes.map(n => {
                    const e = editing[n.key] || { level: 'none', export: false, delete: false };
                    return (
                      <div key={n.key} className="flex items-center gap-3 px-4 py-2 border-b border-slate-50 hover:bg-slate-50/50">
                        <div className="flex-1 text-xs font-display font-semibold text-slate-700">{n.label}</div>
                        <select value={e.level} onChange={ev => setEditing(prev => ({ ...prev, [n.key]: { ...e, level: ev.target.value } }))}
                          disabled={!canEdit}
                          className="text-xs border border-slate-200 rounded-sm px-2 py-0.5 bg-white focus:outline-none focus:border-[#3b4872]">
                          {LEVEL_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                        {n.supports_export && (
                          <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
                            <input type="checkbox" checked={e.export} onChange={ev => setEditing(prev => ({ ...prev, [n.key]: { ...e, export: ev.target.checked } }))} disabled={!canEdit} className="accent-[#3b4872]" /> Export
                          </label>
                        )}
                        {n.supports_delete && (
                          <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
                            <input type="checkbox" checked={e.delete} onChange={ev => setEditing(prev => ({ ...prev, [n.key]: { ...e, delete: ev.target.checked } }))} disabled={!canEdit} className="accent-rose-600" /> Delete
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center py-16 text-slate-400 text-sm">Select a profile to edit its permissions.</div>
        )}
      </div>
    </div>
  );
}

// ─── V2 Panel: Effective Access "View As" ─────────────────────────────────────
const SOURCE_COLORS = {
  super_admin: 'bg-violet-100 text-violet-700',
  override: 'bg-amber-100 text-amber-700',
  senior_profile: 'bg-blue-100 text-blue-700',
  department: 'bg-emerald-100 text-emerald-700',
  open: 'bg-slate-100 text-slate-500',
  denied: 'bg-rose-50 text-rose-600',
};

function EffectiveAccessPanel() {
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUid, setSelectedUid] = useState(null);
  const [effective, setEffective] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query.trim()) return;
    const t = setTimeout(() => {
      apiGet('/user-management/users', { q: query, limit: 20 })
        .then(r => setUsers(r.users || []))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const loadEffective = async (uid) => {
    setSelectedUid(uid); setLoading(true);
    try {
      const r = await apiGet(`/rbac/v2/effective/${uid}`);
      setEffective(r);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    setLoading(false);
  };

  const perms = effective?.permissions || {};
  const byModule = Object.entries(perms).reduce((acc, [key, val]) => {
    const mod = key.split('.')[0];
    (acc[mod] = acc[mod] || []).push({ key, ...val });
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="effective-access-panel">
      <div className="flex gap-3">
        <div className="relative w-72">
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search employee name or email…"
            className="w-full border border-slate-300 rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:border-[#3b4872]"
            data-testid="effective-search" />
          {users.length > 0 && query && (
            <div className="absolute top-full left-0 right-0 bg-white border border-slate-200 rounded-sm shadow-lg z-10 max-h-48 overflow-y-auto">
              {users.map(u => (
                <button key={u.user_id} onClick={() => { loadEffective(u.user_id); setQuery(u.name || u.email); setUsers([]); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b border-slate-100">
                  <div className="font-semibold">{u.name}</div>
                  <div className="text-slate-400">{u.email} · {u.department}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        {effective && (
          <div className="text-sm text-slate-600 flex items-center gap-2">
            <span className="font-semibold">{effective.name}</span> · {effective.department || '—'}
            {effective.senior_profile && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded-sm border border-blue-200">{effective.senior_profile}</span>}
            {effective.is_super_admin && <span className="px-1.5 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded-sm">Super Admin</span>}
          </div>
        )}
      </div>

      {loading && <div className="text-slate-400 text-sm py-8 text-center">Loading permissions…</div>}

      {effective && !loading && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-4 text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">
            <span>Module / Node</span>
            <div className="flex items-center gap-3 ml-auto">
              {Object.entries(SOURCE_COLORS).map(([src, cls]) => (
                <span key={src} className={`px-1.5 py-0.5 rounded-sm ${cls}`}>{src.replace('_', ' ')}</span>
              ))}
            </div>
          </div>
          <div className="overflow-y-auto max-h-[65vh]" data-testid="effective-perms-table">
            {Object.entries(byModule).map(([mod, nodes]) => (
              <div key={mod}>
                <div className="px-4 py-1.5 bg-slate-50 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 border-b border-slate-100">{mod}</div>
                {nodes.map(n => (
                  <div key={n.key} className="flex items-center gap-3 px-4 py-1.5 border-b border-slate-50 hover:bg-slate-50/50">
                    <div className="flex-1 text-xs text-slate-700">{n.key}</div>
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-sm ${SOURCE_COLORS[n.source] || 'bg-slate-100 text-slate-500'}`}>{n.source}</span>
                    <span className="text-[10px] font-mono text-slate-500 w-8 text-right">{n.level}</span>
                    {n.export && <span className="text-[10px] text-emerald-600 font-bold">EXP</span>}
                    {n.delete && <span className="text-[10px] text-rose-600 font-bold">DEL</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── V2 Panel: Consultant Profiles (Phase 3) ─────────────────────────────────
function ConsultantProfilesPanel({ canEdit }) {
  const [profiles, setProfiles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [selectedName, setSelectedName] = useState(null);
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [showNewInput, setShowNewInput] = useState(false);

  useEffect(() => {
    apiGet('/rbac/v2/consultant-profiles').then(r => setProfiles(r.profiles || [])).catch(() => {});
    apiGet('/rbac/v2/catalog').then(r => setCatalog(r.nodes?.filter(n => !n.is_open) || [])).catch(() => {});
  }, []);

  const openProfile = (p) => {
    setSelectedName(p.name);
    const perms = p.permissions || {};
    const init = {};
    catalog.forEach(n => { init[n.key] = perms[n.key] || { level: 'none', export: false, delete: false }; });
    setEditing(init);
  };

  const save = async () => {
    setSaving(true);
    try {
      const enc = encodeURIComponent(selectedName);
      await apiPut(`/rbac/v2/consultant-profiles/${enc}`, { permissions: editing });
      toast.success(`Saved: ${selectedName}`);
      const r = await apiGet('/rbac/v2/consultant-profiles');
      setProfiles(r.profiles || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const createNew = () => {
    if (!newName.trim()) return;
    const init = {};
    catalog.forEach(n => { init[n.key] = { level: 'none', export: false, delete: false }; });
    setSelectedName(newName.trim());
    setEditing(init);
    setShowNewInput(false);
    setNewName('');
  };

  const byModule = catalog.reduce((acc, n) => {
    (acc[n.parent_module] = acc[n.parent_module] || []).push(n);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="consultant-profiles-panel">
      <div className="bg-blue-50 border border-blue-200 rounded-sm px-4 py-3 text-xs text-blue-900">
        <strong>Consultant Profiles</strong> — narrow-access profiles assigned to external consultants and contractors.
        The consultant profile <em>replaces</em> the department profile (no fallthrough). Access expires automatically at contract end date.
      </div>
      <div className="flex items-start gap-4">
        <div className="w-56 shrink-0 bg-white border border-slate-200 rounded-sm p-3">
          <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-2">Profiles</div>
          {profiles.map(p => (
            <button key={p.name} onClick={() => openProfile(p)}
              className={`w-full text-left px-2 py-1.5 rounded-sm text-xs font-display font-semibold mb-0.5 ${selectedName === p.name ? 'bg-[#3b4872] text-white' : 'hover:bg-slate-50 text-slate-700'}`}
              data-testid={`consultant-profile-${p.name}`}>
              {p.name}
            </button>
          ))}
          {canEdit && !showNewInput && (
            <button onClick={() => setShowNewInput(true)}
              className="w-full mt-2 text-[10px] text-[#3b4872] font-display font-bold uppercase tracking-wider flex items-center gap-1 hover:underline"
              data-testid="consultant-profile-new">
              <Plus size={11} /> New Profile
            </button>
          )}
          {showNewInput && (
            <div className="mt-2 space-y-1">
              <input autoFocus value={newName} onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Site Safety Consultant"
                className="w-full text-xs border border-slate-300 rounded-sm px-2 py-1 focus:outline-none focus:border-[#3b4872]"
                onKeyDown={e => e.key === 'Enter' && createNew()}
                data-testid="consultant-profile-name-input" />
              <div className="flex gap-1">
                <button onClick={createNew} className="flex-1 text-[10px] bg-[#3b4872] text-white rounded-sm py-0.5 font-bold">Create</button>
                <button onClick={() => { setShowNewInput(false); setNewName(''); }} className="flex-1 text-[10px] text-slate-500 rounded-sm py-0.5">Cancel</button>
              </div>
            </div>
          )}
        </div>
        {selectedName ? (
          <div className="flex-1 bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="font-display font-bold text-sm flex items-center gap-2">
                <Briefcase size={14} className="text-[#3b4872]" /> {selectedName}
              </div>
              {canEdit && (
                <button onClick={save} disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#3b4872] text-white text-xs font-display font-bold rounded-sm disabled:opacity-50"
                  data-testid="consultant-profile-save">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                </button>
              )}
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              {Object.entries(byModule).map(([mod, nodes]) => (
                <div key={mod} className="border-b border-slate-100">
                  <div className="px-4 py-1.5 bg-slate-50 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">{mod}</div>
                  {nodes.map(n => {
                    const e = editing[n.key] || { level: 'none', export: false, delete: false };
                    return (
                      <div key={n.key} className="flex items-center gap-3 px-4 py-2 border-b border-slate-50 hover:bg-slate-50/50">
                        <div className="flex-1 text-xs font-display font-semibold text-slate-700">{n.label} <span className="text-[10px] font-mono text-slate-400 ml-1">{n.key}</span></div>
                        <select value={e.level} onChange={ev => setEditing(prev => ({ ...prev, [n.key]: { ...e, level: ev.target.value } }))}
                          disabled={!canEdit}
                          className="text-xs border border-slate-200 rounded-sm px-2 py-0.5 bg-white focus:outline-none focus:border-[#3b4872]"
                          data-testid={`cp-level-${n.key}`}>
                          {LEVEL_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                        {n.supports_export && (
                          <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
                            <input type="checkbox" checked={e.export} onChange={ev => setEditing(prev => ({ ...prev, [n.key]: { ...e, export: ev.target.checked } }))} disabled={!canEdit} className="accent-[#3b4872]" /> Export
                          </label>
                        )}
                        {n.supports_delete && (
                          <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
                            <input type="checkbox" checked={e.delete} onChange={ev => setEditing(prev => ({ ...prev, [n.key]: { ...e, delete: ev.target.checked } }))} disabled={!canEdit} className="accent-rose-600" /> Delete
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center py-16 text-slate-400 text-sm">Select or create a consultant profile to configure access.</div>
        )}
      </div>
    </div>
  );
}



// ─── V2 Panel: Per-user Exceptions — full lifecycle (Phase 1) ────────────────
const LEVEL_ORDER = { none: 0, view: 1, edit: 2 };

function ExceptionsPanel({ canEdit }) {
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [saving, setSaving] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // {user_id, name, email, department, overrides:[]}

  const load = async () => {
    setLoading(true);
    try {
      const [exc, cat] = await Promise.all([
        apiGet('/rbac/v2/exceptions'),
        apiGet('/rbac/v2/catalog'),
      ]);
      setUsers(exc.users || []);
      setCatalog((cat.nodes || []).filter(n => !n.is_open));
    } catch { toast.error('Failed to load exceptions'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const removeOverride = async (userId, nodeKey) => {
    setSaving(`${userId}-${nodeKey}`);
    try {
      await apiDel(`/rbac/v2/exceptions/${userId}/${encodeURIComponent(nodeKey)}`);
      toast.success('Override removed');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setSaving(null);
  };

  const clearAll = async (userId) => {
    if (!window.confirm('Clear all overrides for this user?')) return;
    setSaving(`${userId}-all`);
    try {
      await apiDel(`/rbac/v2/exceptions/${userId}`);
      toast.success('All overrides cleared');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setSaving(null);
  };

  const openAdd = () => { setEditTarget(null); setEditorOpen(true); };
  const openEdit = (u) => { setEditTarget(u); setEditorOpen(true); };

  const filtered = users.filter(u => {
    const nameMatch = !search || (u.name || u.email || '').toLowerCase().includes(search.toLowerCase());
    const deptMatch = !deptFilter || u.department === deptFilter;
    return nameMatch && deptMatch;
  });
  const allDepts = [...new Set(users.map(u => u.department).filter(Boolean))].sort();

  return (
    <div className="space-y-4" data-testid="exceptions-panel">
      <div className="flex items-center justify-between">
        <div className="bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 text-xs text-amber-900 flex-1 mr-4">
          <strong>Per-user overrides</strong> — permissions that differ from the department profile.
          Every override is audited (who, when, reason). Expired overrides fall through to the department baseline automatically.
        </div>
        {canEdit && (
          <button onClick={openAdd} className="flex items-center gap-1.5 px-3 py-2 bg-[#3b4872] text-white text-xs font-display font-bold rounded-sm whitespace-nowrap" data-testid="exception-add-btn">
            <Plus size={12} /> Add Exception
          </button>
        )}
      </div>
      <div className="flex gap-2">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…"
          className="flex-1 text-xs border border-slate-200 rounded-sm px-2.5 py-1.5 focus:outline-none focus:border-[#3b4872]"
          data-testid="exceptions-search" />
        <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}
          className="text-xs border border-slate-200 rounded-sm px-2 py-1.5 bg-white focus:outline-none focus:border-[#3b4872]"
          data-testid="exceptions-dept-filter">
          <option value="">All Departments</option>
          {allDepts.map(d => <option key={d}>{d}</option>)}
        </select>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-sm text-slate-400">
          {users.length === 0 ? 'No per-user overrides found.' : 'No results match your filter.'}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(u => (
            <div key={u.user_id} className="bg-white border border-slate-200 rounded-sm" data-testid={`exception-row-${u.user_id}`}>
              <div className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-slate-50"
                onClick={() => setExpanded(prev => prev === u.user_id ? null : u.user_id)}>
                <div>
                  <span className="font-display font-bold text-sm text-slate-800">{u.name || u.email}</span>
                  <span className="text-[10px] text-slate-400 ml-2">{u.department}</span>
                  {u.senior_profile && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 rounded ml-2">{u.senior_profile}</span>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-bold">{u.override_count} override{u.override_count !== 1 ? 's' : ''}</span>
                  {canEdit && (
                    <>
                      <button onClick={e => { e.stopPropagation(); openEdit(u); }}
                        className="text-[10px] text-[#3b4872] hover:underline font-bold flex items-center gap-0.5"
                        data-testid={`exception-edit-${u.user_id}`}>
                        Edit
                      </button>
                      <button onClick={e => { e.stopPropagation(); clearAll(u.user_id); }}
                        disabled={saving === `${u.user_id}-all`}
                        className="text-[10px] text-rose-500 hover:text-rose-700 font-bold flex items-center gap-0.5 disabled:opacity-50"
                        data-testid={`exception-clear-all-${u.user_id}`}>
                        {saving === `${u.user_id}-all` ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />} Clear all
                      </button>
                    </>
                  )}
                  <ChevronRight size={14} className={`text-slate-400 transition-transform ${expanded === u.user_id ? 'rotate-90' : ''}`} />
                </div>
              </div>
              {expanded === u.user_id && (
                <div className="border-t border-slate-100">
                  {(u.overrides || []).map(ov => (
                    <div key={ov.node_key} className="flex items-start gap-3 px-4 py-2 border-b border-slate-50 hover:bg-slate-50/50">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-mono text-slate-700">{ov.node_key}</div>
                        {ov.reason && <div className="text-[10px] text-slate-400 mt-0.5 italic">"{ov.reason}"</div>}
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          {ov.set_by && `by ${ov.set_by}`}
                          {ov.set_at && ` · ${_fmtD(ov.set_at)}`}
                          {ov.expires_at && ` · expires ${_fmtD(ov.expires_at)}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <div className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                          dept: {ov.dept_baseline_level || '—'}
                        </div>
                        <div className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${ov.expired ? 'bg-slate-100 text-slate-400 line-through' : 'bg-amber-100 text-amber-700'}`}>
                          {ov.override_level || '—'}
                        </div>
                        {ov.expired
                          ? <span className="text-[10px] text-slate-400 font-bold">EXPIRED</span>
                          : ov.raises_access
                            ? <span className="text-[10px] text-emerald-600 font-bold">↑</span>
                            : <span className="text-[10px] text-rose-500 font-bold">↓</span>}
                      </div>
                      {canEdit && (
                        <button onClick={() => removeOverride(u.user_id, ov.node_key)}
                          disabled={saving === `${u.user_id}-${ov.node_key}`}
                          className="text-rose-400 hover:text-rose-600 disabled:opacity-50 flex-shrink-0"
                          data-testid={`exception-remove-${u.user_id}-${ov.node_key}`}>
                          {saving === `${u.user_id}-${ov.node_key}` ? <Loader2 size={10} className="animate-spin" /> : <X size={10} />}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editorOpen && (
        <ExceptionEditorModal
          target={editTarget}
          catalog={catalog}
          onClose={() => setEditorOpen(false)}
          onSaved={() => { setEditorOpen(false); load(); }}
        />
      )}
    </div>
  );
}


// ─── Exception Editor Modal (Add / Edit) ────────────────────────────────────
function ExceptionEditorModal({ target, catalog, onClose, onSaved }) {
  // target=null → add mode (search for user first)
  // target={user_id, name, email, department, overrides:[]} → edit mode
  const [step, setStep] = useState(target ? 'nodes' : 'user');
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(target ? { user_id: target.user_id, name: target.name, email: target.email, department: target.department } : null);
  const [nodeOverrides, setNodeOverrides] = useState(() => {
    // Pre-fill from existing overrides when editing
    if (!target) return {};
    const m = {};
    (target.overrides || []).forEach(ov => {
      m[ov.node_key] = {
        enabled: true,
        level: ov.override_level || 'none',
        export: ov.override_export || false,
        delete: ov.override_delete || false,
        reason: ov.reason || '',
        expires_at: ov.expires_at || '',
      };
    });
    return m;
  });
  const [nodeSearch, setNodeSearch] = useState('');
  const [globalReason, setGlobalReason] = useState('');
  const [globalExpiry, setGlobalExpiry] = useState('');
  const [saving, setSaving] = useState(false);

  // User search debounce
  useEffect(() => {
    if (step !== 'user' || !userQuery.trim()) { setUserResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await apiGet(`/rbac/v2/user-search?q=${encodeURIComponent(userQuery)}`);
        setUserResults(r.users || []);
      } catch {} finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [userQuery, step]);

  const selectUser = (u) => { setSelectedUser(u); setStep('nodes'); };

  const toggleNode = (nodeKey) => {
    setNodeOverrides(prev => {
      if (prev[nodeKey]) {
        const { [nodeKey]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [nodeKey]: { enabled: true, level: 'view', export: false, delete: false, reason: '', expires_at: '' } };
    });
  };

  const updateNode = (nodeKey, field, value) => {
    setNodeOverrides(prev => ({ ...prev, [nodeKey]: { ...(prev[nodeKey] || {}), [field]: value } }));
  };

  const save = async () => {
    if (!selectedUser) { toast.error('Select a user first'); return; }
    const overrides = Object.entries(nodeOverrides).map(([node_key, v]) => ({
      node_key,
      level: v.level,
      export: v.export,
      delete: v.delete,
      reason: v.reason || globalReason,
      expires_at: v.expires_at || globalExpiry || undefined,
    }));
    if (!overrides.length) { toast.error('Select at least one node'); return; }
    setSaving(true);
    try {
      await apiPost(`/rbac/v2/exceptions/${selectedUser.user_id}`, { overrides });
      toast.success(`Exceptions saved for ${selectedUser.name || selectedUser.email}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  // Catalog grouped by parent_module
  const byModule = catalog.reduce((acc, n) => {
    (acc[n.parent_module] = acc[n.parent_module] || []).push(n);
    return acc;
  }, {});
  const filteredCatalog = nodeSearch
    ? catalog.filter(n => n.key.toLowerCase().includes(nodeSearch.toLowerCase()) || n.label.toLowerCase().includes(nodeSearch.toLowerCase()))
    : null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="exception-editor-modal">
      <div className="bg-white rounded-sm border border-slate-200 w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="font-display font-bold text-sm">
            {target ? `Edit Exceptions — ${target.name || target.email}` : 'Add Exception'}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>

        {/* Step 1: User search (add mode only) */}
        {step === 'user' && (
          <div className="flex-1 p-5 space-y-4 overflow-y-auto">
            <p className="text-xs text-slate-500">Search for the user to grant an exception to. Searches name, email, or employee ID.</p>
            <input
              autoFocus
              value={userQuery}
              onChange={e => setUserQuery(e.target.value)}
              placeholder="Type name, email, or emp ID…"
              className="w-full text-sm border border-slate-200 rounded-sm px-3 py-2 focus:outline-none focus:border-[#3b4872]"
              data-testid="exception-user-search-input"
            />
            {searching && <div className="text-xs text-slate-400">Searching…</div>}
            {userResults.length > 0 && (
              <div className="border border-slate-200 rounded-sm divide-y divide-slate-100">
                {userResults.map(u => (
                  <button key={u.user_id} onClick={() => selectUser(u)}
                    className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center justify-between"
                    data-testid={`exception-user-result-${u.user_id}`}>
                    <div>
                      <span className="text-sm font-display font-semibold text-slate-800">{u.name}</span>
                      <span className="text-xs text-slate-400 ml-2">{u.email}</span>
                    </div>
                    <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{u.department}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Node picker */}
        {step === 'nodes' && (
          <>
            <div className="px-5 pt-4 pb-2 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-display font-semibold text-slate-700">
                  {selectedUser?.name} <span className="text-slate-400 font-normal">({selectedUser?.department})</span>
                </div>
                <span className="text-[10px] text-slate-400">{Object.keys(nodeOverrides).length} node{Object.keys(nodeOverrides).length !== 1 ? 's' : ''} selected</span>
              </div>
              <input value={nodeSearch} onChange={e => setNodeSearch(e.target.value)}
                placeholder="Filter nodes…"
                className="w-full text-xs border border-slate-200 rounded-sm px-2.5 py-1.5 focus:outline-none focus:border-[#3b4872] bg-white" />
            </div>
            <div className="flex-1 overflow-y-auto">
              {/* Node list */}
              {(filteredCatalog ? [{ _flat: filteredCatalog }] : Object.entries(byModule)).map((entry) => {
                const [modName, nodes] = Array.isArray(entry) ? entry : ['', entry._flat];
                return (
                  <div key={modName || 'filtered'}>
                    {modName && <div className="px-4 py-1.5 bg-slate-50 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 sticky top-0 z-10">{modName}</div>}
                    {nodes.map(n => {
                      const ov = nodeOverrides[n.key];
                      return (
                        <div key={n.key} className={`border-b border-slate-50 ${ov ? 'bg-blue-50/50' : ''}`}>
                          <div className="flex items-center gap-2 px-4 py-2 hover:bg-slate-50/60">
                            <input type="checkbox" checked={!!ov} onChange={() => toggleNode(n.key)}
                              className="accent-[#3b4872] flex-shrink-0"
                              data-testid={`exc-node-toggle-${n.key}`} />
                            <div className="flex-1 min-w-0">
                              <span className="text-xs font-display font-semibold text-slate-700">{n.label}</span>
                              <span className="text-[9px] font-mono text-slate-400 ml-1.5">{n.key}</span>
                            </div>
                            {ov && (
                              <div className="flex items-center gap-2">
                                <select value={ov.level} onChange={e => updateNode(n.key, 'level', e.target.value)}
                                  className="text-[10px] border border-slate-200 rounded-sm px-1 py-0.5 bg-white focus:outline-none focus:border-[#3b4872]"
                                  data-testid={`exc-level-${n.key}`}>
                                  {['none', 'view', 'edit'].map(l => <option key={l}>{l}</option>)}
                                </select>
                                {n.supports_export && (
                                  <label className="text-[9px] text-slate-500 flex items-center gap-0.5">
                                    <input type="checkbox" checked={ov.export} onChange={e => updateNode(n.key, 'export', e.target.checked)} className="accent-[#3b4872]" /> Exp
                                  </label>
                                )}
                                {n.supports_delete && (
                                  <label className="text-[9px] text-slate-500 flex items-center gap-0.5">
                                    <input type="checkbox" checked={ov.delete} onChange={e => updateNode(n.key, 'delete', e.target.checked)} className="accent-rose-600" /> Del
                                  </label>
                                )}
                              </div>
                            )}
                          </div>
                          {ov && (
                            <div className="flex items-center gap-2 px-10 pb-2">
                              <input value={ov.reason} onChange={e => updateNode(n.key, 'reason', e.target.value)}
                                placeholder="Reason (optional)…"
                                className="flex-1 text-[10px] border border-slate-200 rounded-sm px-2 py-0.5 focus:outline-none focus:border-[#3b4872]"
                                data-testid={`exc-reason-${n.key}`} />
                              <input type="date" value={ov.expires_at ? ov.expires_at.slice(0, 10) : ''}
                                onChange={e => updateNode(n.key, 'expires_at', e.target.value ? e.target.value + 'T00:00:00Z' : '')}
                                className="text-[10px] border border-slate-200 rounded-sm px-2 py-0.5 focus:outline-none focus:border-[#3b4872]"
                                placeholder="Expires" title="Optional expiry date"
                                data-testid={`exc-expiry-${n.key}`} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          {step === 'nodes' && !target && (
            <button onClick={() => setStep('user')} className="text-xs text-slate-500 hover:text-slate-700">← Change user</button>
          )}
          {step === 'user' && <span />}
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
            {step === 'nodes' && (
              <button onClick={save} disabled={saving || !Object.keys(nodeOverrides).length}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-[#3b4872] text-white text-xs font-display font-bold rounded-sm disabled:opacity-50"
                data-testid="exception-editor-save">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Save Exceptions
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}



// ─── V2 Panel: Audit Trail — every department-profile permission change ─────
const LEVEL_BADGE = { none: 'bg-slate-100 text-slate-400', view: 'bg-blue-100 text-blue-700', edit: 'bg-amber-100 text-amber-700' };

function AuditTrailPanel() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    apiGet('/rbac/v2/department-profile-audit', { limit: 50 })
      .then(r => setEntries(r?.entries || []))
      .catch(() => toast.error('Failed to load audit trail'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4" data-testid="audit-trail-panel">
      <div className="bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-xs text-slate-600">
        <strong>Audit Trail</strong> — every department-profile permission save, across all departments, newest
        first. Shows exactly which nodes changed level and who changed them (backed by <code>department_profile_history</code>).
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-slate-400" /></div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-sm text-slate-400" data-testid="audit-trail-empty">No permission changes recorded yet.</div>
      ) : (
        <div className="space-y-2">
          {entries.map((e, i) => {
            const rowKey = `${e.department}-${e.changed_at}-${i}`;
            return (
              <div key={rowKey} className="bg-white border border-slate-200 rounded-sm" data-testid={`audit-trail-row-${i}`}>
                <div className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-slate-50"
                  onClick={() => setExpanded(prev => prev === rowKey ? null : rowKey)}>
                  <div>
                    <span className="font-display font-bold text-sm text-slate-800">{e.department}</span>
                    <span className="text-[10px] text-slate-400 ml-2">
                      by {e.changed_by || 'unknown'} · {e.changed_at ? _fmtDT(e.changed_at) : '—'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-bold">
                      {e.changed_count} node{e.changed_count !== 1 ? 's' : ''} changed
                    </span>
                    <ChevronRight size={14} className={`text-slate-400 transition-transform ${expanded === rowKey ? 'rotate-90' : ''}`} />
                  </div>
                </div>
                {expanded === rowKey && (
                  <div className="border-t border-slate-100">
                    {(e.changed_nodes || []).map(n => (
                      <div key={n.node} className="flex items-center gap-3 px-4 py-2 border-b border-slate-50 hover:bg-slate-50/50" data-testid={`audit-trail-node-${n.node}`}>
                        <div className="flex-1 text-xs font-mono text-slate-700">{n.node}</div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${LEVEL_BADGE[n.before] || LEVEL_BADGE.none}`}>{n.before}</span>
                        <span className="text-slate-300">→</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${LEVEL_BADGE[n.after] || LEVEL_BADGE.none}`}>{n.after}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



// ─── V2 Panel: Enforce Impact Preview (§3, 23-Sep-2026) ──────────────────────
function ImpactPreviewPanel({ isSuperAdmin, onModeChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);   // department name whose denied list is open
  const [confirmMode, setConfirmMode] = useState(null); // 'enforce' | 'shadow' | null
  const [switching, setSwitching] = useState(false);

  const load = () => {
    setLoading(true);
    apiGet('/rbac/v2/impact-preview')
      .then(r => setData(r))
      .catch(e => toast.error(e?.response?.data?.detail || 'Failed to load impact preview'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const applyMode = async () => {
    setSwitching(true);
    try {
      const r = await apiPut('/rbac/v2/flag', { mode: confirmMode });
      toast.success(`RBAC mode set to ${r.rbac_mode.toUpperCase()}`);
      onModeChanged?.(r.rbac_mode);
      setConfirmMode(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to change mode');
    } finally { setSwitching(false); }
  };

  if (loading) return <div className="p-10 text-center" data-testid="impact-loading"><Loader2 size={20} className="animate-spin inline mr-2" />Computing impact…</div>;
  if (!data) return null;
  const h = data.headline;
  const depts = [...data.departments].sort((a, b) => b.active_users - a.active_users);

  const Card = ({ tone, title, big, children, testid }) => (
    <div className={`rounded-sm border p-4 ${tone === 'rose' ? 'border-rose-300 bg-rose-50' : tone === 'amber' ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`} data-testid={testid}>
      <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">{title}</div>
      <div className={`text-2xl font-display font-bold mt-1 ${tone === 'rose' ? 'text-rose-700' : tone === 'amber' ? 'text-amber-700' : 'text-slate-800'}`}>{big}</div>
      {children && <div className="text-xs text-slate-600 mt-1 break-words">{children}</div>}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="impact-preview-panel">
      {/* plain-language explainer */}
      <div className="rounded-sm border border-indigo-200 bg-indigo-50 px-4 py-3 text-xs text-indigo-900">
        <strong>What this shows.</strong> If you switch to <strong>Enforce</strong> now, this is exactly which
        screens each department keeps or loses, and how many staff are affected — computed against the live
        permission profiles. Nothing here changes any setting. Ellora reviews this, then decides.
        Current mode: <strong>{(data.mode_live || '').toUpperCase()}</strong> · {data.total_screens} screens evaluated.
      </div>

      {/* headline cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card tone={h.departments_fully_locked_out.length ? 'rose' : 'slate'} title="Fully locked out" big={h.departments_fully_locked_out.length} testid="impact-locked-card">
          {h.departments_fully_locked_out.length ? `${h.users_fully_locked_out} staff · ${h.departments_fully_locked_out.join(', ')}` : 'No department loses all access.'}
        </Card>
        <Card tone={h.departments_without_profile.length ? 'rose' : 'slate'} title="Depts with users but NO profile" big={h.departments_without_profile.length} testid="impact-noprofile-card">
          {h.departments_without_profile.length ? `${h.users_without_profile} staff limited to open screens only · ${h.departments_without_profile.join(', ')}` : 'Every staffed department has a profile.'}
        </Card>
        <Card tone={h.departments_with_profile_errors.length ? 'amber' : 'slate'} title="Profile errors" big={h.departments_with_profile_errors.length} testid="impact-errors-card">
          {h.departments_with_profile_errors.length ? `Grants a screen that no longer exists · ${h.departments_with_profile_errors.join(', ')}` : 'No stale grants found.'}
        </Card>
        <Card tone="slate" title="Shadow denials logged" big={h.denial_log_total} testid="impact-denials-card">
          {h.denial_log_empty ? 'Empty — shadow logging has no record yet.' : 'Real record of access staff have needed.'}
        </Card>
      </div>

      {/* mode toggle */}
      {isSuperAdmin && (
        <div className="rounded-sm border border-slate-200 bg-white px-4 py-3 flex flex-wrap items-center gap-3" data-testid="impact-toggle-row">
          <div className="text-xs text-slate-700">
            Enforcement mode: <strong className={data.mode_live === 'enforce' ? 'text-emerald-700' : 'text-rose-700'}>{(data.mode_live || '').toUpperCase()}</strong>
          </div>
          {data.mode_live === 'shadow' ? (
            <button onClick={() => setConfirmMode('enforce')} className="px-3 py-1.5 text-[11px] font-display font-bold uppercase bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856]" data-testid="impact-switch-enforce">
              Switch to Enforce…
            </button>
          ) : (
            <button onClick={() => setConfirmMode('shadow')} className="px-3 py-1.5 text-[11px] font-display font-bold uppercase border border-rose-300 text-rose-700 rounded-sm hover:bg-rose-50" data-testid="impact-switch-shadow">
              Switch to Shadow…
            </button>
          )}
        </div>
      )}

      {/* per-department table */}
      <div className="overflow-x-auto rounded-sm border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left py-2 px-3">Department</th>
              <th className="text-right py-2 px-3">Active staff</th>
              <th className="text-right py-2 px-3">Screens kept</th>
              <th className="text-right py-2 px-3">Screens lost</th>
              <th className="text-left py-2 px-3">Status</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {depts.map(r => (
              <React.Fragment key={r.department}>
                <tr className={`border-t border-slate-100 ${r.fully_locked_out ? 'bg-rose-50' : (!r.profile_exists && r.active_users > 0) ? 'bg-amber-50' : ''}`} data-testid={`impact-row-${r.department}`}>
                  <td className="py-2 px-3 font-medium text-slate-800">{r.department}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{r.active_users}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-emerald-700">{r.allowed_count}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-rose-700">{r.denied_count}</td>
                  <td className="py-2 px-3">
                    {r.fully_locked_out && <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase rounded-sm bg-rose-100 text-rose-700">Locked out</span>}
                    {!r.fully_locked_out && !r.profile_exists && r.active_users > 0 && <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase rounded-sm bg-amber-100 text-amber-800">No profile</span>}
                    {r.stale_grants.length > 0 && <span className="ml-1 inline-block px-2 py-0.5 text-[10px] font-bold uppercase rounded-sm bg-amber-100 text-amber-800" title={r.stale_grants.join(', ')}>{r.stale_grants.length} stale</span>}
                    {r.fully_locked_out === false && r.profile_exists && r.stale_grants.length === 0 && <span className="text-[11px] text-slate-400">ok</span>}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button onClick={() => setExpanded(expanded === r.department ? null : r.department)} className="text-[11px] text-[#3b4872] hover:underline inline-flex items-center gap-0.5" data-testid={`impact-expand-${r.department}`}>
                      {expanded === r.department ? 'Hide' : 'Screens'} <ChevronRight size={12} className={expanded === r.department ? 'rotate-90 transition-transform' : 'transition-transform'} />
                    </button>
                  </td>
                </tr>
                {expanded === r.department && (
                  <tr className="border-t border-slate-100 bg-slate-50/60">
                    <td colSpan={6} className="py-3 px-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-[10px] font-display font-bold uppercase text-emerald-700 mb-1">Keeps ({r.allowed_screens.length})</div>
                          <div className="flex flex-wrap gap-1">
                            {r.allowed_screens.map(s => <span key={s.key} className="px-1.5 py-0.5 text-[10px] rounded-sm bg-emerald-50 text-emerald-800 border border-emerald-200">{s.label}</span>)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] font-display font-bold uppercase text-rose-700 mb-1">Loses ({r.denied_screens.length})</div>
                          <div className="flex flex-wrap gap-1">
                            {r.denied_screens.map(s => <span key={s.key} className="px-1.5 py-0.5 text-[10px] rounded-sm bg-rose-50 text-rose-800 border border-rose-200">{s.label}</span>)}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* confirm dialog */}
      {confirmMode && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setConfirmMode(null); }}>
          <div className="bg-white rounded-sm w-full max-w-lg p-5" data-testid="impact-confirm-dialog">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert size={18} className={confirmMode === 'enforce' ? 'text-[#3b4872]' : 'text-rose-600'} />
              <h3 className="text-base font-display font-bold text-slate-800">Switch to {confirmMode.toUpperCase()}?</h3>
            </div>
            {confirmMode === 'enforce' ? (
              <div className="text-sm text-slate-700 space-y-2">
                <p>Permissions will start being enforced immediately. Based on the current profiles:</p>
                <ul className="text-xs list-disc pl-5 space-y-1">
                  <li><strong>{h.departments_fully_locked_out.length}</strong> department(s) fully locked out ({h.users_fully_locked_out} staff){h.departments_fully_locked_out.length ? `: ${h.departments_fully_locked_out.join(', ')}` : ''}</li>
                  <li><strong>{h.departments_without_profile.length}</strong> staffed department(s) with no profile ({h.users_without_profile} staff) will see only open screens</li>
                  <li><strong>{h.departments_with_profile_errors.length}</strong> department(s) have profile errors (stale grants)</li>
                </ul>
                {(h.departments_fully_locked_out.length > 0 || h.departments_without_profile.length > 0) && (
                  <p className="text-xs text-rose-700 font-semibold">Staff in the affected departments will lose access to screens the moment you confirm.</p>
                )}
              </div>
            ) : (
              <div className="text-sm text-slate-700 space-y-2">
                <p>Shadow mode <strong>disables enforcement</strong>: every permission check will pass and any authenticated user can read all data (including payroll, PAN, UAN). Denials are only logged.</p>
                <p className="text-xs text-rose-700 font-semibold">Use only as a temporary hold. Do not leave in shadow for long.</p>
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirmMode(null)} className="px-3 py-1.5 text-xs font-display font-bold uppercase text-slate-600 hover:text-slate-800" data-testid="impact-confirm-cancel">Cancel</button>
              <button onClick={applyMode} disabled={switching} className={`px-3 py-1.5 text-xs font-display font-bold uppercase text-white rounded-sm disabled:opacity-60 ${confirmMode === 'enforce' ? 'bg-[#3b4872] hover:bg-[#2d3856]' : 'bg-rose-600 hover:bg-rose-700'}`} data-testid="impact-confirm-apply">
                {switching ? <Loader2 size={12} className="animate-spin inline" /> : `Yes, set ${confirmMode}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

